/**
 * CheckPopovers — Designer-side popover wrappers for the add + drop CHECK
 * forms. Sqlite renders disabled triggers with the recreate-path tooltip; the
 * form bodies are never mounted on sqlite. Mirrors `UniquePopovers` from
 * Slice B3.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B4.
 */
import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import {
  getAddCheckConstraintUnsupportedTooltip,
  isAddCheckConstraintSupported,
} from '../../edit/edits/addCheckConstraint';
import {
  getDropCheckConstraintUnsupportedTooltip,
  isDropCheckConstraintSupported,
} from '../../edit/edits/dropCheckConstraint';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { CheckAddFormBody } from './CheckAddFormBody';
import { CheckDropFormBody } from './CheckDropFormBody';
import { DisabledAffordance } from './DisabledAffordance';

export interface CheckAddPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly onCommitted?: () => void;
}

export function CheckAddPopover(props: CheckAddPopoverProps) {
  const { editContext, testId = 'check-add-popover', onCommitted } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isAddCheckConstraintSupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getAddCheckConstraintUnsupportedTooltip(engine)}
        variant="ghost"
      >
        <Plus className="h-3.5 w-3.5" />
        Check
      </DisabledAffordance>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Add check constraint"
        >
          <Plus className="h-3.5 w-3.5" />
          Check
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="max-h-[min(480px,var(--radix-popover-content-available-height))] w-80 overflow-y-auto"
        data-testid={testId}
      >
        {open ? (
          <CheckAddFormBody
            editContext={editContext}
            testId={testId}
            onCommitted={() => {
              setOpen(false);
              onCommitted?.();
            }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export interface CheckDropPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly onCommitted?: () => void;
}

export function CheckDropPopover(props: CheckDropPopoverProps) {
  const { editContext, testId = 'check-drop-popover', onCommitted } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isDropCheckConstraintSupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getDropCheckConstraintUnsupportedTooltip(engine)}
        variant="ghost"
      >
        <Trash2 className="h-3.5 w-3.5" />
        Drop check
      </DisabledAffordance>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Drop check constraint"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Drop check
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="w-80"
        data-testid={testId}
      >
        {open ? (
          <CheckDropFormBody
            editContext={editContext}
            testId={testId}
            onCommitted={() => {
              setOpen(false);
              onCommitted?.();
            }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
